import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import { checkmark } from '@ibk/shared-assets';
import { ContentLayout, StepperCard } from '@ibk/shared-layout';
import { useTranslation } from '@ibk/shared-translation';
import { ProductFamily } from '@ibk/shared-types';
import { Loading } from '@ibk/shared-ui';
import { useEmployeesData } from '../../../../hooks';
import { useGetEmissorProductTermsQuery } from '../../../../services/emissorBenefitsApi';
import { HiringStep, SidebarStatus } from '../../../../types/hiring';
import { ContractSummarySidebar } from '../ContractSummarySidebar';
import styles from './HiringTerms.module.css';
export function HiringTerms({ onBack, onComplete, onNext, contractData, organizationId, sidebarSteps, address, employeeCount, getSidebarSteps, currentProductFamily, productFamilies, isLastTermsStep, stepNumber, isPatEnabled = false, isEmissorFlow = false, isSubmitting, headerStepper, }) {
    const { t } = useTranslation();
    const { employeesData } = useEmployeesData();
    const { data: emissorTerms, isFetching: isEmissorTermsLoading, isError: isEmissorTermsError, } = useGetEmissorProductTermsQuery(undefined, { skip: !isEmissorFlow });
    const emissorTermVersion = emissorTerms?.terms?.termVersion;
    const emissorTermsHtml = emissorTermVersion?.sourceType === 'HTML' ? emissorTermVersion.sourceValue : undefined;
    const [isTermsAccepted, setIsTermsAccepted] = useState(false);
    const [isIframeLoaded, setIsIframeLoaded] = useState(false);
    const [iframeError, setIframeError] = useState(false);
    const [loadingTimeout, setLoadingTimeout] = useState(null);
    const contractPath = isPatEnabled && currentProductFamily === ProductFamily.PAT ? 'dominio-beneficios-pat' : 'dominio-beneficios';
    const contractUrl = `${globalThis.location.origin}/${contractPath}`;
    useEffect(() => {
        setIsTermsAccepted(false);
    }, [currentProductFamily, isEmissorFlow, organizationId]);
    useEffect(() => {
        if (isEmissorFlow) {
            return;
        }
        const timeout = setTimeout(() => {
            if (!isIframeLoaded && !iframeError) {
                setIframeError(true);
            }
        }, 10000);
        setLoadingTimeout(timeout);
        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [isEmissorFlow, isIframeLoaded, iframeError]);
    const handleIframeLoad = useCallback(() => {
        setIsIframeLoaded(true);
        setIframeError(false);
        if (loadingTimeout) {
            clearTimeout(loadingTimeout);
            setLoadingTimeout(null);
        }
    }, [loadingTimeout]);
    const handleIframeError = useCallback(() => {
        console.warn('Erro ao carregar iframe');
        setIframeError(true);
        setIsIframeLoaded(false);
        if (loadingTimeout) {
            clearTimeout(loadingTimeout);
            setLoadingTimeout(null);
        }
    }, [loadingTimeout]);
    const handleTermsChange = useCallback((checked) => {
        setIsTermsAccepted(checked);
    }, []);
    const handleActivate = useCallback(async () => {
        if (!isTermsAccepted)
            return;
        if (isLastTermsStep === false && onNext) {
            onNext();
            return;
        }
        if (onComplete) {
            await onComplete({
                organizationId,
                contractData,
                isTermsAccepted,
            });
        }
    }, [isTermsAccepted, organizationId, contractData, onComplete, onNext, isLastTermsStep]);
    const breadcrumbs = [
        { label: t('contract.breadcrumb.benefits'), href: '/beneficios' },
        { label: t('hiring.breadcrumb.hiring'), isActive: true },
    ];
    const getStepTitle = () => {
        if (currentProductFamily) {
            const familyLabel = currentProductFamily === ProductFamily.AUXILIO
                ? t('hiring.product-family.option.auxilio.title')
                : t('hiring.product-family.option.pat.title');
            return `${t('hiring.step.contract')} - ${familyLabel}`;
        }
        return t('contract.step.signature');
    };
    const currentStep = {
        id: HiringStep.TERMS,
        title: getStepTitle(),
        number: stepNumber ?? 3,
        status: SidebarStatus.ACTIVE,
    };
    const termsContentBlocked = isEmissorFlow
        ? isEmissorTermsError || isEmissorTermsLoading || !emissorTermsHtml
        : iframeError;
    const isButtonsDisabled = () => isSubmitting;
    const shouldShowLoadingIndicator = () => isSubmitting;
    const shouldRenderLoadingCard = () => isSubmitting;
    const renderContractContent = () => {
        if (isEmissorFlow) {
            if (isEmissorTermsError) {
                return (_jsx("div", { className: styles.errorContainer, "data-testid": 'viewHiringTermsErrorContainer', children: _jsx("div", { className: styles.errorText, "data-testid": 'textHiringTermsErrorMessage', children: t('contract.error.loading') }) }));
            }
            if (isEmissorTermsLoading || !emissorTermsHtml) {
                return (_jsx("div", { className: styles.loadingContainer, "data-testid": 'viewHiringTermsLoadingContainer', children: _jsx("div", { className: styles.loadingText, "data-testid": 'textHiringTermsLoadingMessage', children: t('contract.loading') }) }));
            }
            return (_jsx("div", { className: styles.contractHtml, "data-testid": 'viewHiringTermsEmissorHtml', dangerouslySetInnerHTML: { __html: emissorTermsHtml } }));
        }
        if (iframeError) {
            return (_jsx("div", { className: styles.errorContainer, "data-testid": 'viewHiringTermsErrorContainer', children: _jsx("div", { className: styles.errorText, "data-testid": 'textHiringTermsErrorMessage', children: t('contract.error.loading') }) }));
        }
        return (_jsxs(_Fragment, { children: [!isIframeLoaded && (_jsx("div", { className: styles.loadingContainer, "data-testid": 'viewHiringTermsLoadingContainer', children: _jsx("div", { className: styles.loadingText, "data-testid": 'textHiringTermsLoadingMessage', children: t('contract.loading') }) })), _jsx("iframe", { src: contractUrl, className: styles.contractIframe, onLoad: handleIframeLoad, onError: handleIframeError, title: t('contract.step.signature'), sandbox: 'allow-scripts allow-same-origin allow-popups', referrerPolicy: 'no-referrer-when-downgrade', style: { display: isIframeLoaded ? 'block' : 'none' }, "data-testid": 'iframeHiringTermsContract' })] }));
    };
    const renderLoadingMainCard = () => {
        const loadingStep = {
            id: HiringStep.TERMS,
            title: getStepTitle(),
            number: stepNumber ?? 3,
            status: SidebarStatus.ACTIVE,
        };
        return (_jsx(StepperCard, { currentStep: loadingStep, showStepper: !headerStepper, children: _jsx("div", { className: styles.stepContent, children: _jsxs("div", { className: styles.loadingContent, children: [_jsx("div", { className: `${styles.iconContainer} ${styles.loadingIconContainer}`, children: _jsx(Loading, { size: 'small' }) }), _jsx("h1", { className: styles.loadingTitle, children: t('activation.loading.title') })] }) }) }));
    };
    const renderNormalMainCard = () => (_jsxs(StepperCard, { currentStep: currentStep, showStepper: !headerStepper, actions: {
            back: {
                label: t('contract.button.back'),
                onClick: onBack,
                disabled: isButtonsDisabled(),
            },
            next: {
                label: isPatEnabled && !isLastTermsStep ? t('hiring.button.next') : t('contract.button.activate'),
                onClick: handleActivate,
                disabled: !isTermsAccepted || isButtonsDisabled() || termsContentBlocked,
                loading: shouldShowLoadingIndicator(),
            },
        }, children: [headerStepper, _jsxs("div", { className: styles.stepContent, "data-testid": 'viewHiringTermsStepContent', children: [_jsx("div", { className: styles.contractContainer, "data-testid": 'viewHiringTermsContractContainer', children: renderContractContent() }), _jsx("div", { className: styles.termsAcceptance, "data-testid": 'viewHiringTermsTermsAcceptance', children: _jsxs("label", { className: styles.checkboxContainer, "data-testid": 'viewHiringTermsCheckboxContainer', children: [_jsx("input", { type: 'checkbox', className: styles.checkbox, checked: isTermsAccepted, onChange: e => handleTermsChange(e.target.checked), disabled: termsContentBlocked, "data-testid": 'checkboxHiringTermsAcceptance' }), _jsx("span", { className: styles.checkmark, children: isTermsAccepted && (_jsx("img", { src: checkmark, alt: '', className: styles.checkmarkIcon, "data-testid": 'imageHiringTermsCheckmark' })) }), _jsx("span", { className: styles.labelText, "data-testid": 'textHiringTermsAcceptanceLabel', children: termsContentBlocked ? t('contract.error.terms-disabled') : t('contract.terms.acceptance') })] }) })] })] }));
    const renderLoadingSummaryCard = () => {
        const displayEmployeeCount = employeesData?.employeeCount ?? employeeCount;
        return (_jsx(ContractSummarySidebar, { steps: getSidebarSteps(true), address: address, employeeCount: displayEmployeeCount, productFamilies: productFamilies }));
    };
    const renderNormalSummaryCard = () => {
        const displayEmployeeCount = employeesData?.employeeCount ?? employeeCount;
        return (_jsx(ContractSummarySidebar, { steps: sidebarSteps, address: address, employeeCount: displayEmployeeCount, productFamilies: productFamilies }));
    };
    const renderMainCard = () => {
        if (shouldRenderLoadingCard()) {
            return renderLoadingMainCard();
        }
        return renderNormalMainCard();
    };
    const renderSummaryCard = () => {
        if (shouldRenderLoadingCard()) {
            return renderLoadingSummaryCard();
        }
        return renderNormalSummaryCard();
    };
    if (headerStepper) {
        return (_jsx(ContentLayout, { breadcrumbs: breadcrumbs, showSidebar: false, noMaxWidth: true, children: renderMainCard() }));
    }
    return (_jsx(ContentLayout, { breadcrumbs: breadcrumbs, sidebar: renderSummaryCard(), children: renderMainCard() }));
}
